import type { Pharmacy } from "@tellescope/types-models";
import { useTranslation } from "react-i18next";
import { cn } from "@tellescope/utils";
import { Check, X } from "lucide-react";

interface PharmacyCardProps {
  pharmacy: Pharmacy;
  onClick?: () => void;
  selected?: boolean;
  onClear?: () => void;
}

const formatPharmacyAddress = (pharmacy: Pharmacy): string => {
  const parts = [pharmacy.addressLine1, pharmacy.city, pharmacy.stateProvince, pharmacy.postalCode].filter(Boolean);
  return parts.join(", ");
};

export const PharmacyCard = ({ pharmacy, onClick, selected, onClear }: PharmacyCardProps) => {
  const { t } = useTranslation("forms");
  const address = formatPharmacyAddress(pharmacy);
  return (
    <div
      role={onClick ? "button" : undefined}
      onClick={onClick}
      className={cn(
        "rounded-md border p-3 text-sm",
        selected ? "border-green-500 bg-green-50 relative" : "hover:bg-gray-50 cursor-pointer"
      )}
    >
      {selected && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onClear?.();
          }}
          className="absolute top-2 right-2 text-muted-foreground hover:text-foreground"
          aria-label={t("Clear selection")}
        >
          <X className="size-4" />
        </button>
      )}
      <div className="flex items-start gap-2">
        {selected && <Check className="size-4 shrink-0 text-green-600" />}
        <div className="min-w-0">
          <p className="font-semibold truncate">{pharmacy.businessName}</p>
          {!!address && <p className="text-muted-foreground text-xs mt-0.5">{address}</p>}
          {!!pharmacy.primaryTelephone && <p className="text-muted-foreground text-xs">{pharmacy.primaryTelephone}</p>}
        </div>
      </div>
    </div>
  );
};
